<template>
  <v-app id="inspire">
    <v-app-bar class="d-flex justify-center" app absolute clipped-right flat height="72">
      <v-responsive max-width="300">
        <ChangeResolution @changeResolution="(value) => changeResolution(value)" />
      </v-responsive>
    </v-app-bar>
    <v-navigation-drawer permanent app width="500">
      <v-app-bar absolute clipped-left flat height="72">
        <ActionsBar @onSave="onSave" />
      </v-app-bar>
      <div class="d-flex flex-row full-height">
        <v-navigation-drawer permanent color="grey darken-3" width="100">
          <ListNavigations
            :onSave="onSave"
            :selected="selected"
            :checkChangedData="checkChangedData"
            @onSelectNavigation="(key) => onSelectNavigation(key)"
          />
        </v-navigation-drawer>
        <v-navigation-drawer permanent width="400">
          <EditDetailPart ref="edit-part" :selected="selected" :reloadIframe="reloadIframe" />
        </v-navigation-drawer>
      </div>
    </v-navigation-drawer>
    <v-main>
      <IFrameLearningPage ref="iframe" :domain="`${learningDomain}` + `${path}`" :resolution="resolution" />
    </v-main>
    <!-- AI Assistant Floating Widget -->
    <div
      v-show="!aiBuilderOpen"
      class="ai-assistance-widget"
    >
      <div
        class="ai-assistant-bubble"
        @click="aiBuilderOpen = true"
      >
        <div class="bubble-header">
          <div class="d-flex align-center">
            <div class="assistant-icon-badge">
              <v-icon size="16" color="white">mdi-creation</v-icon>
            </div>
            <span class="assistant-title">
              {{ $vuetify.lang.t('$vuetify.SITE_BUILDER.AI_BUILDER.ASSISTANT_TITLE') }}
            </span>
          </div>
          <div class="assistant-status-dot" />
        </div>
        <div class="bubble-body">
          {{ $vuetify.lang.t('$vuetify.SITE_BUILDER.AI_BUILDER.ASSISTANT_SUBTITLE') }}
        </div>
        <div class="bubble-arrow" />
      </div>

      <button
        type="button"
        class="ai-assistance-fab ai-assistance-btn"
        :aria-label="$vuetify.lang.t('$vuetify.SITE_BUILDER.AI_BUILDER.BUTTON')"
        @click="aiBuilderOpen = true"
      >
        <v-icon size="26" color="white">mdi-creation</v-icon>
      </button>
    </div>
    <AIBuilderPopup
      ref="aiPopup"
      v-model="aiBuilderOpen"
      :page-key="selected"
      @apply="onAiBuilderApply"
    />
  </v-app>
</template>

<script>
import IFrameLearningPage from '@/modules/SiteBuilder/components/IFrameLearningPage'
import EditDetailPart from '@/modules/SiteBuilder/components/EditDetailPart'
import ListNavigations from '@/modules/SiteBuilder/components/ListNavigations'
import ChangeResolution from '@/modules/SiteBuilder/components/ChangeResolution'
import ActionsBar from '@/modules/SiteBuilder/components/ActionsBar'
import AIBuilderPopup from '@/modules/SiteBuilder/components/AIBuilderPopup'
import {acceptAiBuilderHtml} from '@/modules/SiteBuilder/aiBuilder/acceptAiBuilderHtml'
import {mapGetters, mapActions} from 'vuex'
export default {
  components: {
    ChangeResolution,
    ActionsBar,
    ListNavigations,
    EditDetailPart,
    IFrameLearningPage,
    AIBuilderPopup,
  },
  data() {
    return {
      resolution: '100%',
      path: '',
      selected: 'common',
      aiBuilderOpen: false,
    }
  },
  computed: {
    ...mapGetters({
      learningDomain: 'vendor/getLearningDomain',
    }),
  },
  methods: {
    ...mapActions({
      removeMessageError: 'sitebuilder/removeMessageError',
    }),
    changeResolution(value) {
      this.resolution = value
    },
    onSelectNavigation(key) {
      if (key === 'about-us' || key === 'privacy-policy' || key === 'recruitment') {
        this.path = `/${key}`
      } else this.path = ''
      this.selected = key
    },
    reloadIframe() {
      this.$refs['iframe'].reload()
    },
    async onSave() {
      await this.$refs['edit-part'].onSave()
    },
    async checkChangedData() {
      const isChanged = await this.$refs['edit-part'].checkChangeData()
      return isChanged
    },
    rejectText(reason) {
      const keyByReason = {
        'common-page': 'REJECT_COMMON',
        'no-page': 'REJECT_NO_PAGE',
        'empty-html': 'REJECT_EMPTY',
        'invalid-payload': 'REJECT_INVALID',
        'too-large': 'REJECT_TOO_LARGE',
      }
      const key = keyByReason[reason] || 'REJECT_INVALID'
      return this.$vuetify.lang.t('$vuetify.SITE_BUILDER.AI_BUILDER.' + key)
    },
    onAiBuilderApply(sourceHtml) {
      const result = acceptAiBuilderHtml({sourceHtml: sourceHtml, pageKey: this.selected})
      if (!result.ok) {
        this.$refs.aiPopup.notifyRejected(result.reason)
        this.$message.error(this.rejectText(result.reason))
        return
      }
      const appended = this.$refs['edit-part'].appendHtmlSection(result.section)
      if (!appended.ok) {
        this.$refs.aiPopup.notifyRejected(appended.reason)
        this.$message.error(this.rejectText(appended.reason))
        return
      }
      this.$refs.aiPopup.notifyApplied(result.section.id)
      this.aiBuilderOpen = false
      this.$message.success(this.$vuetify.lang.t('$vuetify.SITE_BUILDER.AI_BUILDER.INSERT_SUCCESS'))
    },
  },
  async created() {
    await this.removeMessageError()
  },
}
</script>

<style scoped>
.ai-assistance-widget {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  user-select: none;
  animation: ai-widget-float 3.8s ease-in-out infinite;
}

.ai-assistance-widget:hover {
  animation-play-state: paused;
}

.ai-assistant-bubble {
  position: relative;
  background: #ffffff;
  border-radius: 18px;
  padding: 12px 18px;
  box-shadow: 0 10px 25px -4px rgba(0, 0, 0, 0.08), 0 4px 12px -2px rgba(0, 0, 0, 0.04);
  border: 1px solid #edf0f5;
  margin-bottom: 12px;
  cursor: pointer;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.ai-assistant-bubble:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 30px -4px rgba(0, 0, 0, 0.12), 0 6px 14px -2px rgba(0, 0, 0, 0.06);
}

.bubble-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 6px;
}

.assistant-icon-badge {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: #255CC2;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.assistant-title {
  font-size: 14px;
  font-weight: 700;
  color: #255CC2;
  margin-left: 8px;
  line-height: 1;
}

.assistant-status-dot {
  position: relative;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #e6f9ed;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.assistant-status-dot::after {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
  animation: ai-status-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.bubble-body {
  font-size: 13.5px;
  font-weight: 500;
  color: #374151;
  line-height: 1.4;
  white-space: nowrap;
}

.bubble-arrow {
  position: absolute;
  bottom: -6px;
  right: 21px;
  width: 12px;
  height: 12px;
  background: #ffffff;
  transform: rotate(45deg);
  border-right: 1px solid #edf0f5;
  border-bottom: 1px solid #edf0f5;
}

.ai-assistance-fab {
  position: relative;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: #255CC2;
  border: none;
  outline: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px -4px rgba(37, 92, 194, 0.45), 0 4px 10px -2px rgba(37, 92, 194, 0.25);
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.ai-assistance-fab::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  border: 2px solid #255CC2;
  animation: ai-ripple 2.8s cubic-bezier(0, 0.2, 0.8, 1) infinite;
  pointer-events: none;
}

.ai-assistance-fab .v-icon {
  transition: transform 0.3s ease;
  animation: ai-sparkle-twinkle 3s ease-in-out infinite;
}

.ai-assistance-fab:hover {
  transform: translateY(-4px) scale(1.06);
  box-shadow: 0 14px 30px -4px rgba(37, 92, 194, 0.6), 0 6px 14px -2px rgba(37, 92, 194, 0.35);
}

.ai-assistance-fab:hover::after {
  animation-play-state: paused;
  opacity: 0;
}

.ai-assistance-fab:hover .v-icon {
  animation: none;
  transform: scale(1.15) rotate(12deg);
}

.ai-assistance-fab:active {
  transform: translateY(0) scale(0.98);
}

@keyframes ai-widget-float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

@keyframes ai-ripple {
  0% {
    transform: scale(0.95);
    opacity: 0.85;
  }
  60% {
    transform: scale(1.4);
    opacity: 0;
  }
  100% {
    transform: scale(1.4);
    opacity: 0;
  }
}

@keyframes ai-sparkle-twinkle {
  0%, 100% {
    transform: scale(1) rotate(0deg);
  }
  30% {
    transform: scale(1.12) rotate(6deg);
  }
  60% {
    transform: scale(0.95) rotate(-4deg);
  }
}

@keyframes ai-status-pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.35);
    opacity: 0.6;
  }
}
</style>
